import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import Typography from "@mui/material/Typography";
import { v4 as uuid } from "uuid";
import { makeStyles } from "tss-react/mui";
import { Switch } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ReactSortable } from "react-sortablejs";
import IntentButton, { Intent } from "../input/intent-button";
import RecipeMethodStep from "./recipe-method-step";
import { MethodStep } from "../../types";

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
  },

  emptyText: {
    color: theme.palette.grey.A200,
    padding: "10px 0",
  },

  newStepButton: {
    display: "flex",
    justifyContent: "center",
    margin: "10px auto 0 auto",
  },

  showAllSwitch: {
    marginLeft: "10px",
  },

  dragHandle: {
    position: "absolute",
    left: "5px",
    color: theme.palette.grey.A200,
  },
}));

interface Props {
  method?: MethodStep[];
  setMethod?: (method: MethodStep[]) => void;
  editMode?: boolean;
}

export default function RecipeMethod({
  method = [],
  setMethod = () => {},
  editMode = false,
}: Props) {
  const [expanded, setExpanded] = useState("");
  const [expandAll, setExpandAll] = useState(false);
  const { classes } = useStyles();

  function handleExpansionChange(id: any, isExpanded: any) {
    setExpandAll(false);
    setExpanded(isExpanded ? id : "");
  }

  function addStep() {
    const newId = uuid();
    setMethod([...method, { id: newId, step: "" }]);
    setExpanded(newId);
  }

  function updateStep(step: any) {
    const newMethod = method.map((s: any) => {
      if (s.id === step.id) {
        return step;
      }
      return s;
    });
    setMethod(newMethod);
  }

  function deleteStep(stepId: any) {
    const newMethod = [...method];
    const index = newMethod.findIndex((step) => step.id === stepId);
    newMethod.splice(index, 1);
    setMethod(newMethod);
  }

  return (
    <Box className={classes.root}>
      <FormControlLabel
        control={
          <Switch
            className={classes.showAllSwitch}
            checked={expandAll}
            onChange={() => setExpandAll(!expandAll)}
            inputProps={{ "aria-label": "show all steps" }}
            value="showAll"
          />
        }
        label="Show All"
      />
      {method.length ? (
        <ReactSortable
          list={method}
          setList={setMethod}
          handle={`.${classes.dragHandle}`}
        >
          {method.map((s, index) => (
            <RecipeMethodStep
              expanded={expandAll || expanded === s.id}
              editMode={editMode}
              key={s.id}
              updateStep={updateStep}
              step={s}
              handleExpansionChange={handleExpansionChange}
              deleteStep={deleteStep}
              stepName={`Step ${index + 1}`}
              handleClass={classes.dragHandle}
            />
          ))}
        </ReactSortable>
      ) : (
        <Typography
          className={classes.emptyText}
          variant="body1"
          align="center"
        >
          No Method
        </Typography>
      )}
      {editMode && (
        <IntentButton
          className={classes.newStepButton}
          onClick={addStep}
          intent={Intent.PRIMARY}
          startIcon={<AddIcon />}
          variant="contained"
        >
          Add Step
        </IntentButton>
      )}
    </Box>
  );
}
